import { useEffect, useMemo, useRef, useState } from 'react';
import { logChannelRequestFailure } from 'setup/logger/logChannelRequestFailure';
import type { AnalyticsChart } from 'shared/domain/analytics/chartModel';
import { chartFiltersClient } from 'shared/domain/chartFilters/chartFiltersClient';
import { ChartFiltersId, toChartFiltersId } from 'shared/domain/chartFilters/chartFiltersModel';
import type { FilterValues } from 'shared/types/analytics';
import { createLatestRead } from 'store/latestRead';
import { useProjectId, useProjectTimezone } from 'store/project';
import {
  extractFiltersValues,
  getChartFiltersSignature,
  moveDefaultDateRangeToToday,
} from './filterValues';

interface SavedChartFilters {
  chartId: ChartFiltersId;
  filters: FilterValues;
}

/**
 * Saved filters of a chart in the selected project, and saving of the
 * current ones. Without saved filters (or when they cannot be read) the chart
 * starts with `defaultValues`.
 *
 * Filters are saved only once the saved ones of the same project and chart
 * were read, so filters of another project are never saved under this one.
 */
export function useChartFiltersPersistence(
  chart: AnalyticsChart,
  filters: FilterValues | undefined,
  defaultValues: () => FilterValues
): { savedFilters: FilterValues | undefined } {
  const projectId = useProjectId();
  const timezone = useProjectTimezone();
  const chartId = useMemo(
    () => (projectId ? toChartFiltersId(projectId, chart) : undefined),
    [projectId, chart]
  );
  const [saved, setSaved] = useState<SavedChartFilters | undefined>(undefined);
  const savedSignatureRef = useRef<string | null>(null);
  // Read when the chart opens, not again when these change: a new read would
  // replace the filters the user is choosing.
  const timezoneRef = useRef(timezone);
  timezoneRef.current = timezone;
  const defaultValuesRef = useRef(defaultValues);
  defaultValuesRef.current = defaultValues;

  useEffect(() => {
    if (!chartId) return;

    savedSignatureRef.current = null;
    const latestRead = createLatestRead();
    void latestRead.run(() => chartFiltersClient.getSaved(chartId), {
      onResult: (savedFilters) =>
        setSaved({
          chartId,
          filters: savedFilters
            ? moveDefaultDateRangeToToday(savedFilters, timezoneRef.current)
            : defaultValuesRef.current(),
        }),
      onError: (error) => {
        logChannelRequestFailure('Could not read saved chart filters', error, { chart });
        setSaved({ chartId, filters: defaultValuesRef.current() });
      },
    });
    return latestRead.cancel;
  }, [chartId, chart]);

  const isReadForChart = saved !== undefined && saved.chartId === chartId;

  useEffect(() => {
    if (!chartId || !filters || !isReadForChart) return;

    const signature = getChartFiltersSignature(filters);
    if (savedSignatureRef.current === signature) return;
    savedSignatureRef.current = signature;

    chartFiltersClient.save(chartId, extractFiltersValues(filters)).catch((error: unknown) => {
      // Not saved: the next change (or the same one again) tries again.
      if (savedSignatureRef.current === signature) {
        savedSignatureRef.current = null;
      }
      logChannelRequestFailure('Could not save chart filters', error, { chart });
    });
  }, [chartId, chart, filters, isReadForChart]);

  return useMemo(() => ({ savedFilters: isReadForChart ? saved.filters : undefined }), [isReadForChart, saved]);
}
